/**
 * RelationshipFinderPage — "How am I related?" at /family/:slug/relate
 *
 * Two member pickers → calls GET /family-trees/:slug/relationship-path?a=&b=
 * → renders the kinship label + the full path with photos.
 *
 * Deep link: /family/:slug/relate?a=<mid>&b=<mid>  (shareable)
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Users, Loader2, Search as SearchIcon, X } from "lucide-react";
import { http } from "@/lib/api";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  saffronBg: "#FFF7ED",
  parchment: "#FFFBF4",
  line: "rgba(194,65,12,0.14)",
};

function MemberPicker({ members, value, onChange, label, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = members.find((m) => m.id === value);
  const matches = useMemo(() => {
    if (!q.trim()) return members.slice(0, 20);
    const s = q.trim().toLowerCase();
    return members
      .filter((m) =>
        (m.full_name || "").toLowerCase().includes(s)
        || (m.name_ml || "").includes(q)
      )
      .slice(0, 30);
  }, [q, members]);

  return (
    <div className="relative" data-testid={`${testid}-wrap`}>
      <label className="block text-[11px] font-semibold tracking-widest uppercase mb-1.5" style={{ color: P.inkSoft }}>
        {label}
      </label>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors"
        style={{ background: "#fff", border: `1px solid ${P.line}`, color: P.ink }}
      >
        {selected?.photo ? (
          <img src={selected.photo} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold" style={{ background: P.saffronBg, color: P.saffron }}>
            {selected ? (selected.full_name || "?").charAt(0) : "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {selected ? selected.full_name : "Select a family member…"}
          </div>
          {selected?.name_ml && (
            <div className="text-xs truncate" style={{ color: P.inkSoft }}>{selected.name_ml}</div>
          )}
        </div>
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-2xl overflow-hidden z-30"
          style={{ background: "#fff", border: `1px solid ${P.line}`, boxShadow: "0 20px 40px -16px rgba(194,65,12,0.3)" }}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: P.line }}>
            <SearchIcon className="w-4 h-4" style={{ color: P.inkSoft }} />
            <input
              autoFocus
              type="text"
              placeholder="Search by name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid={`${testid}-search`}
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear" className="text-xs" style={{ color: P.inkSoft }}>
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {matches.length === 0 ? (
              <div className="text-sm px-4 py-6 text-center" style={{ color: P.inkSoft }}>
                No matches
              </div>
            ) : (
              matches.map((m) => (
                <button
                  key={m.id}
                  data-testid={`${testid}-opt-${m.id}`}
                  type="button"
                  onClick={() => { onChange(m.id); setOpen(false); setQ(""); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-orange-50 transition-colors"
                >
                  {m.photo ? (
                    <img src={m.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: P.saffronBg, color: P.saffron }}>
                      {(m.full_name || "?").charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: P.ink }}>{m.full_name}</div>
                    {m.name_ml && (
                      <div className="text-xs truncate" style={{ color: P.inkSoft }}>{m.name_ml}</div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const EDGE_WORDS = {
  self: "You",
  parent: "parent",
  child: "child",
  sibling: "sibling",
  spouse: "spouse",
};

export default function RelationshipFinderPage() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [a, setA] = useState(sp.get("a") || "");
  const [b, setB] = useState(sp.get("b") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [treeName, setTreeName] = useState("");

  // Load the tree + members
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const t = await http.get(`/family-trees/${slug}`);
        if (cancel) return;
        setTreeName(t.data?.name || "");
        const m = await http.get(`/family-trees/${t.data.id}/members`);
        if (cancel) return;
        const list = (m.data.items || []).map((x) => ({
          id: x.id,
          full_name: [x.first_name, x.last_name].filter(Boolean).join(" ") || "Unknown",
          name_ml: x.name_ml,
          photo: x.photo,
        })).sort((p, q) => p.full_name.localeCompare(q.full_name));
        setMembers(list);
      } catch { /* 403 private, 404 missing — handled by tree page */ }
    })();
    return () => { cancel = true; };
  }, [slug]);

  // Compute whenever a + b are both selected
  useEffect(() => {
    if (!a || !b || a === b) {
      setResult(null);
      return;
    }
    setLoading(true);
    const next = new URLSearchParams(sp);
    next.set("a", a); next.set("b", b);
    setSp(next, { replace: true });
    http.get(`/family-trees/${slug}/relationship-path?a=${a}&b=${b}`)
      .then((r) => setResult(r.data))
      .catch((e) => setResult({ found: false, description: e?.response?.data?.detail || "Couldn't compute path" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, slug]);

  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}/family/${slug}/relate`
    : "";
  const title = `How are we related? · ${treeName || "Family"} · IndianFamilyTree`;

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10" data-testid="relationship-finder-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content="Discover how any two members of your family tree are related." />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <div className="mb-6">
        <Link to={`/family/${slug}`} className="text-xs font-semibold uppercase tracking-widest" style={{ color: P.saffron }}>
          ← Back to {treeName || "the family tree"}
        </Link>
        <h1 className="font-display text-3xl lg:text-4xl mt-3" style={{ color: P.ink }}>
          How are we related?
        </h1>
        <p className="text-sm lg:text-base mt-2" style={{ color: P.inkSoft }}>
          Pick two people from your family. We'll trace the path through parents, siblings, spouses and generations to show exactly how they're connected.
        </p>
      </div>

      <div
        className="rounded-3xl p-5 lg:p-7 mb-6"
        style={{
          background: `linear-gradient(135deg, ${P.saffronBg} 0%, ${P.parchment} 100%)`,
          border: `1px solid ${P.line}`,
          boxShadow: "0 16px 40px -24px rgba(194,65,12,0.25)",
        }}
      >
        <div className="grid md:grid-cols-2 gap-4">
          <MemberPicker members={members} value={a} onChange={setA} label="Person A" testid="rf-pick-a" />
          <MemberPicker members={members} value={b} onChange={setB} label="Person B" testid="rf-pick-b" />
        </div>
        {a && b && a === b && (
          <div className="mt-4 text-sm" style={{ color: P.inkSoft }}>
            Pick two different people.
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-sm py-6" style={{ color: P.inkSoft }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.saffron }} />
          Tracing the path…
        </div>
      )}

      {!loading && result && !result.found && (
        <div
          data-testid="rf-no-relationship"
          className="rounded-2xl p-5 text-sm"
          style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.inkSoft }}
        >
          <Users className="w-5 h-5 mb-2" style={{ color: P.saffron }} />
          We couldn't find a known relationship between these two people in this tree. Try adding missing parents, siblings or spouses first.
        </div>
      )}

      {!loading && result && result.found && (
        <div data-testid="rf-result" className="space-y-4">
          <div
            className="rounded-3xl p-6 lg:p-8 text-center"
            style={{
              background: `linear-gradient(135deg, #fff 0%, ${P.saffronBg} 100%)`,
              border: `1px solid ${P.line}`,
            }}
          >
            <div className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: P.saffron }}>
              Relationship
            </div>
            <div className="font-display text-3xl lg:text-4xl mt-2" style={{ color: P.ink }} data-testid="rf-description">
              {result.description || "related"}
            </div>
            <div className="text-xs mt-2" style={{ color: P.inkSoft }}>
              {result.distance} {result.distance === 1 ? "step" : "steps"} across the family
            </div>
          </div>

          {/* The path */}
          <div
            className="rounded-3xl p-5 lg:p-7"
            style={{ background: "#fff", border: `1px solid ${P.line}` }}
          >
            <div className="text-[11px] font-semibold tracking-widest uppercase mb-4" style={{ color: P.inkSoft }}>
              Path
            </div>
            <ol className="space-y-3">
              {(result.path || []).map((step, i) => (
                <li key={`${step.sp_id || step.member_id || i}`} className="flex items-center gap-3">
                  <div className="shrink-0">
                    {step.photo ? (
                      <img src={step.photo} alt="" className="w-11 h-11 rounded-full object-cover" />
                    ) : (
                      <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold" style={{ background: P.saffronBg, color: P.saffron }}>
                        {(step.name || "?").charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate" style={{ color: P.ink }}>{step.name}</div>
                    {step.name_ml && (
                      <div className="text-xs truncate" style={{ color: P.inkSoft }}>{step.name_ml}</div>
                    )}
                  </div>
                  {step.edge && step.edge !== "self" && (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
                      style={{ background: "rgba(194,65,12,0.1)", color: P.saffron }}
                    >
                      <ArrowRight className="w-3 h-3" />
                      {EDGE_WORDS[step.edge] || step.edge}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {!loading && !result && (!a || !b) && (
        <div
          className="rounded-2xl p-5 text-sm text-center"
          style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.inkSoft }}
        >
          Pick two people above to see how they're connected.
        </div>
      )}
    </div>
  );
}
